'use client';
import { PUBLIC } from '@/constants/image/path';
import { type Table } from '@tanstack/react-table';
import { default as classNames, default as classnames } from 'classnames';
import Image from 'next/image';

type PaginationProps = {
  table: Table<any>;
  currentPage?: number;
  totalPage?: number;
  goToPrev?: () => void;
  goToNext?: () => void;
  setPage?: (index: number) => void;
};

/**
 * @param table // useReactTable() react-table產生的物件
 * @param {number} currentPage 目前頁數
 * @param {number} totalPage 總頁數
 * @param {function} goToPrev 回前一頁
 * @param {function} goToNext 去下一頁
 * @param {function} setPage 到第n頁
 * @returns
 */
const Pagination = ({ table, currentPage = 0, totalPage = 0, goToPrev, goToNext, setPage }: PaginationProps) => {
  const current = table?.getState()?.pagination?.pageIndex || currentPage;
  const total = table?.getPageCount() || totalPage;
  const keys = [...Array(total).keys()];
  const filterKeys = keys.filter((page: number) => Math.abs(page - current) <= 2);
  const pages = current < 2 ? keys.slice(0, 5) : current >= total - 2 ? keys.slice(total - 5, total) : filterKeys;

  const onClickPrevNext = (direct: 1 | -1, enabled: boolean) => () => {
    if (!enabled) return;
    if (direct > 0) {
      table?.nextPage();
      goToNext?.();
    }
    if (direct < 0) {
      table.previousPage();
      goToPrev?.();
    }
  };

  const canPreviousPage = table?.getCanPreviousPage();
  const canNextPage = table?.getCanNextPage();
  const leftArrow = canPreviousPage
    ? `${PUBLIC}/icons/arrows/page-left.svg`
    : `${PUBLIC}/icons/arrows/page-left-disabled.svg`;
  const rightArrow = canNextPage
    ? `${PUBLIC}/icons/arrows/page-right.svg`
    : `${PUBLIC}/icons/arrows/page-right-disabled.svg`;

  const onClickPage = (index: number) => () => {
    table?.setPageIndex(index);
    setPage?.(index);
  };

  return (
    <div className='font-sm mt-4 flex items-center justify-center text-[#515c69]'>
      <button onClick={onClickPrevNext(-1, canPreviousPage)}>
        <Image
          className={classnames('mr-2 cursor-pointer', {
            'cursor-not-allowed': !canPreviousPage
          })}
          alt='left-arrow'
          src={leftArrow}
          width={12}
          height={12}
        />
      </button>
      <ul className='flex items-center'>
        {pages.map((index: number) => {
          const isActive = index === current;

          return (
            <li
              key={`page-${index}`}
              className={classNames('flex h-6 w-6 cursor-pointer items-center justify-center', {
                'rounded bg-[#e8edf6] ': isActive
              })}
            >
              <button onClick={onClickPage(index)}>{index + 1}</button>
            </li>
          );
        })}
      </ul>
      <button onClick={onClickPrevNext(1, canNextPage)}>
        <Image
          className={classnames('ml-2 cursor-pointer', {
            'cursor-not-allowed': !canNextPage
          })}
          alt='right-arrow'
          src={rightArrow}
          width={12}
          height={12}
        />
      </button>
    </div>
  );
};

export default Pagination;
